// src/services/appointmentSlot.service.ts
import api from '../lib/api';

const API_URL = import.meta.env.VITE_APPOINTMENTS_API_URL;

// ---------------------------------------------------------
// Helper to build URLs consistently
// ---------------------------------------------------------
function url(company: string, path: string) {
  return `${API_URL}/api/${company}${path}`;
}

// ---------------------------------------------------------
// Types
// ---------------------------------------------------------
export interface SlotItem {
  _id: string;
  slot: string; // "09:00-09:30"
  isBooked: boolean;
}

export interface GenerateSlotPayload {
  date: string; // "YYYY-MM-DD"
  startTime: string; // "09:00"
  endTime: string; // "17:00"
  duration: number; // e.g., 30
  staff?: string;
  service?: string;
}

export interface BatchGeneratePayload {
  startDate: string;
  endDate: string;
  weekdays: number[]; // [1..7], Monday=1, Sunday=7
  startTime: string;
  endTime: string;
  duration: number;
  staff?: string;
  service?: string;
  skipDates?: string[];
  dryRun?: boolean;
}

export type FetchSlotsResponse =
  | SlotItem[]
  | {
      data?: SlotItem[];
      slots?: SlotItem[];
      results?: SlotItem[];
      items?: SlotItem[];
    };

// ---------------------------------------------------------
// SERVICES
// ---------------------------------------------------------

// Get month-level calendar status
// GET /:company/calendar?month=YYYY-MM
export async function fetchCalendarStatus(company: string, month: string) {
  try {
    const res = await api.get(url(company, '/calendar'), {
      params: { month },
    });
    return res.data; // expected: [{ date, slots: [...] }, ...]
  } catch (error) {
    console.error('[fetchCalendarStatus] Error:', error);
    throw error;
  }
}

/**
 * GET /:company/slots?date=YYYY-MM-DD
 * Fetch available slots for a specific date
 */
export async function fetchSlots(
  company: string,
  date: string,
): Promise<FetchSlotsResponse> {
  try {
    const res = await api.get(url(company, '/slots'), {
      params: { date },
    });
    return res.data as FetchSlotsResponse;
  } catch (error) {
    console.error('[fetchSlots] Error:', error);
    throw error;
  }
}

/**
 * POST /:company/slots/book
 * Check if slot is bookable (pre-flight validation)
 */
export async function prebookSlot(
  company: string,
  payload: { slotId?: string; date?: string; slot?: string },
) {
  try {
    const res = await api.post(url(company, '/slots/book'), payload);
    return res.data;
  } catch (error: any) {
    console.error('[prebookSlot] Error:', error);
    throw error.response?.data || error;
  }
}

/**
 * POST /:company/slots/unbook
 * Admin API — unbook a slot
 */
export async function unbookSlot(
  company: string,
  payload: { slotId?: string; date?: string; slot?: string },
  apiKey: string,
) {
  try {
    const res = await api.post(url(company, '/slots/unbook'), payload, {
      headers: { 'x-api-key': apiKey },
    });
    return res.data;
  } catch (error) {
    console.error('[unbookSlot] Error:', error);
    throw error;
  }
}

/**
 * GET /:company/slots/all
 * Admin API — get all slots for the company
 */
export async function fetchAllSlots(company: string) {
  try {
    const res = await api.get(url(company, '/slots/all'));
    return res.data;
  } catch (error) {
    console.error('[fetchAllSlots] Error:', error);
    throw error;
  }
}

/**
 * POST /:company/slots/generate
 * Admin API — generate slots for a single day
 */
export async function generateSlots(
  company: string,
  payload: GenerateSlotPayload,
  apiKey: string,
) {
  try {
    const res = await api.post(url(company, '/slots/generate'), payload, {
      headers: { 'x-api-key': apiKey },
    });
    return res.data;
  } catch (error) {
    console.error('[generateSlots] Error:', error);
    throw error;
  }
}

/**
 * POST /:company/slots/generate/batch
 * Admin API — generate slots for many days at once
 */
export async function generateSlotsBatch(
  company: string,
  payload: BatchGeneratePayload,
  apiKey: string,
) {
  try {
    const res = await api.post(url(company, '/slots/generate/batch'), payload, {
      headers: { 'x-api-key': apiKey },
    });
    return res.data;
  } catch (error) {
    console.error('[generateSlotsBatch] Error:', error);
    throw error;
  }
}

/**
 * DELETE /:company/slots/:slotId
 * Admin API — delete a single slot (unbooked only)
 */
export async function deleteSlot(
  company: string,
  slotId: string,
  apiKey: string,
) {
  try {
    const res = await api.delete(url(company, `/slots/${slotId}`), {
      headers: { 'x-api-key': apiKey },
    });
    return res.data;
  } catch (error) {
    console.error('[deleteSlot] Error:', error);
    throw error;
  }
}

/**
 * DELETE /:company/slots/by-date/:date
 * Admin API — delete all slots for a specific date
 */
export async function deleteSlotsByDate(
  company: string,
  date: string,
  apiKey: string,
) {
  try {
    const res = await api.delete(url(company, `/slots/by-date/${date}`), {
      headers: { 'x-api-key': apiKey },
    });
    return res.data;
  } catch (error) {
    console.error('[deleteSlotsByDate] Error:', error);
    throw error;
  }
}
